import React, { useState, useRef, useEffect } from 'react';
import { FiSettings, FiCalendar, FiDownload, FiFileText, FiFile, FiMove, FiTruck, FiEdit2 } from 'react-icons/fi';
import axios from 'axios';
import {
  PRICE_COST_WITHOUT_VAT,
  PRICE_COST_WITHOUT_VAT_RUB,
  PRICE_RECOMMENDED_RETAIL,
  PRICE_RECOMMENDED_RETAIL_RUB,
} from '../constants/equipmentLabels';
import { positionsLabel } from '../utils/pluralRu';
import { isNewItem } from '../utils/equipmentNew';
import EquipmentComment from './EquipmentComment';

// Версия раскладки колонок в localStorage: поднимаем, когда меняем колонки по умолчанию.
const LAYOUT_VERSION = 2;

function EquipmentTable({
  equipmentList,
  onBook,
  selectedItems,
  setSelectedItems,
  canEdit = false,
  onEdit = null,
  allowBooking = true,
  settingsStorageKey = 'equipmentTableSettings',
  exportEndpoint = '/export-equipment',
  exportFilenamePrefix = 'equipment_catalog',
  exportIdField = 'equipment_ids',
  extraColumns = [],
  hiddenColumnKeys = [],
  title = 'Список оборудования',
  emptyText = 'Оборудование не найдено',
}) {
  const extraKeys = extraColumns.map((col) => col.key);
  const isHidden = (key) => hiddenColumnKeys.includes(key);

  // Загрузка сохраненных настроек из localStorage
  const loadSettings = () => {
    const saved = localStorage.getItem(settingsStorageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Миграция: добавляем in_transit если его нет в сохраненных настройках
        if (parsed.columnSettings && !('in_transit' in parsed.columnSettings) && !isHidden('in_transit')) {
          parsed.columnSettings.in_transit = true;
        }
        if (parsed.columnOrder && !parsed.columnOrder.includes('in_transit') && !isHidden('in_transit')) {
          // Вставляем после 'available'
          const availableIndex = parsed.columnOrder.indexOf('available');
          if (availableIndex !== -1) {
            parsed.columnOrder.splice(availableIndex + 1, 0, 'in_transit');
          } else {
            parsed.columnOrder.push('in_transit');
          }
        }
        if (parsed.columnWidths && !('in_transit' in parsed.columnWidths) && !isHidden('in_transit')) {
          parsed.columnWidths.in_transit = 120;
        }
        if (parsed.columnSettings && !('warehouse' in parsed.columnSettings)) {
          parsed.columnSettings.warehouse = true;
        }
        if (parsed.columnSettings && !('comment' in parsed.columnSettings)) {
          parsed.columnSettings.comment = false;
        }
        if (parsed.columnOrder && !parsed.columnOrder.includes('warehouse')) {
          const locationIndex = parsed.columnOrder.indexOf('location');
          if (locationIndex !== -1) {
            parsed.columnOrder.splice(locationIndex + 1, 0, 'warehouse');
          } else {
            parsed.columnOrder.push('warehouse');
          }
        }
        if (parsed.columnOrder && !parsed.columnOrder.includes('comment')) {
          parsed.columnOrder.push('comment');
        }
        if (parsed.columnWidths && !('warehouse' in parsed.columnWidths)) {
          parsed.columnWidths.warehouse = 140;
        }
        if (parsed.columnWidths && !('comment' in parsed.columnWidths)) {
          parsed.columnWidths.comment = 220;
        }
        if (parsed.columnSettings && !('price_retail' in parsed.columnSettings)) {
          parsed.columnSettings.price_retail = true;
        }
        if (parsed.columnOrder && !parsed.columnOrder.includes('price_retail')) {
          const priceDdpIndex = parsed.columnOrder.indexOf('price_ddp');
          if (priceDdpIndex !== -1) {
            parsed.columnOrder.splice(priceDdpIndex + 1, 0, 'price_retail');
          } else {
            parsed.columnOrder.push('price_retail');
          }
        }
        if (parsed.columnWidths && !('price_retail' in parsed.columnWidths)) {
          parsed.columnWidths.price_retail = 170;
        }
        extraColumns.forEach((col) => {
          if (parsed.columnSettings && !(col.key in parsed.columnSettings)) {
            parsed.columnSettings[col.key] = col.defaultVisible !== false;
          }
          if (parsed.columnOrder && !parsed.columnOrder.includes(col.key)) {
            parsed.columnOrder.unshift(col.key);
          }
          if (parsed.columnWidths && !(col.key in parsed.columnWidths)) {
            parsed.columnWidths[col.key] = col.width || 160;
          }
        });
        // Раскладка 2 (10.2026): Sales должен видеть комментарий и тип сразу — один раз
        // включаем обе колонки и ставим их следом за наименованием. Дальше выбор человека.
        // Тип — только на своём складе: у партнёров «категория» — их собственный текст.
        if ((parsed.layoutVersion || 1) < LAYOUT_VERSION) {
          const promote = (allowBooking ? ['comment', 'category'] : ['comment']).filter((key) => !isHidden(key));
          promote.forEach((key) => {
            if (parsed.columnSettings) parsed.columnSettings[key] = true;
          });
          if (parsed.columnOrder) {
            const rest = parsed.columnOrder.filter((key) => !promote.includes(key));
            const nameIndex = rest.indexOf('name');
            rest.splice(nameIndex === -1 ? 0 : nameIndex + 1, 0, ...promote);
            parsed.columnOrder = rest;
          }
          if (parsed.columnWidths && !('category' in parsed.columnWidths)) {
            parsed.columnWidths.category = 140;
          }
          parsed.layoutVersion = LAYOUT_VERSION;
        }
        if (parsed.columnOrder) {
          parsed.columnOrder = parsed.columnOrder.filter((key) => !isHidden(key));
        }
        return parsed;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const savedSettings = loadSettings();

  const extraDefaults = extraColumns.reduce((acc, col) => {
    acc.settings[col.key] = col.defaultVisible !== false;
    acc.order.push(col.key);
    acc.widths[col.key] = col.width || 160;
    return acc;
  }, { settings: {}, order: [], widths: {} });

  const defaultColumnSettings = {
    ...extraDefaults.settings,
    name: true,
    vendor: true,
    model: true,
    configuration: true,
    description: true,
    quantity: true,
    available: true,
    price_ddp: true,
    price_retail: true,
    price_gpl: true,
    code_1c: true,
    in_transit: true,
    category: allowBooking,
    location: false,
    warehouse: true,
    comment: true,
  };
  hiddenColumnKeys.forEach((key) => {
    delete defaultColumnSettings[key];
  });

  const defaultColumnOrder = [
    ...extraDefaults.order,
    'name', 'comment', 'category', 'vendor', 'model', 'configuration', 'description',
    'quantity', 'available', 'in_transit', 'price_ddp', 'price_retail', 'price_gpl', 'code_1c',
    'location', 'warehouse',
  ].filter((key) => !isHidden(key));

  const defaultColumnWidths = {
    ...extraDefaults.widths,
    name: 200,
    vendor: 120,
    model: 120,
    configuration: 150,
    description: 250,
    quantity: 80,
    available: 100,
    in_transit: 120,
    price_ddp: 150,
    price_retail: 170,
    price_gpl: 200,
    code_1c: 120,
    category: 120,
    location: 120,
    warehouse: 140,
    comment: 220,
  };

  // Настройки видимости колонок
  const [columnSettings, setColumnSettings] = useState(savedSettings?.columnSettings || defaultColumnSettings);

  // Порядок колонок
  const [columnOrder, setColumnOrder] = useState(savedSettings?.columnOrder || defaultColumnOrder);

  // Ширина колонок
  const [columnWidths, setColumnWidths] = useState(savedSettings?.columnWidths || defaultColumnWidths);

  const [showSettings, setShowSettings] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [showWidthSettings, setShowWidthSettings] = useState(false);
  const [tempColumnWidths, setTempColumnWidths] = useState({});

  const tableRef = useRef(null);

  // Сохранение настроек при изменении
  useEffect(() => {
    const settings = {
      columnSettings,
      columnOrder,
      columnWidths,
      layoutVersion: LAYOUT_VERSION,
    };
    localStorage.setItem(settingsStorageKey, JSON.stringify(settings));
  }, [columnSettings, columnOrder, columnWidths, settingsStorageKey]);

  const toggleColumn = (column) => {
    setColumnSettings(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const openWidthSettings = () => {
    setTempColumnWidths({ ...columnWidths });
    setShowWidthSettings(true);
  };

  const updateColumnWidth = (columnKey, width) => {
    setColumnWidths(prev => ({ ...prev, [columnKey]: Math.max(50, parseInt(width) || 50) }));
  };

  const updateTempColumnWidth = (columnKey, width) => {
    setTempColumnWidths(prev => ({ ...prev, [columnKey]: Math.max(50, parseInt(width) || 50) }));
  };

  const applyWidthChanges = () => {
    setColumnWidths(prev => ({ ...prev, ...tempColumnWidths }));
    setTempColumnWidths({});
    setShowWidthSettings(false);
  };

  const cancelWidthChanges = () => {
    setTempColumnWidths({});
    setShowWidthSettings(false);
  };

  const resetColumnWidths = () => {
    const defaultWidths = {
      name: 200,
      vendor: 120,
      model: 120,
      configuration: 150,
      description: 250,
      quantity: 80,
      available: 100,
      in_transit: 120,
      price_ddp: 150,
      price_gpl: 200,
      code_1c: 120,
      category: 120,
      location: 120,
      warehouse: 140,
      comment: 220,
    };
    extraColumns.forEach((col) => {
      defaultWidths[col.key] = col.width || 160;
    });
    hiddenColumnKeys.forEach((key) => {
      delete defaultWidths[key];
    });
    setColumnWidths(defaultWidths);
    setTempColumnWidths({});
  };

  const toggleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    if (setSelectedItems) {
      setSelectedItems(newSelected);
    }
  };

  const toggleSelectAll = () => {
    if (setSelectedItems) {
      if (selectedItems.size === equipmentList.length) {
        setSelectedItems(new Set());
      } else {
        setSelectedItems(new Set(equipmentList.map(item => item.id)));
      }
    }
  };

  const bookSelectedItems = () => {
    if (!allowBooking || !onBook) return;
    const items = equipmentList.filter(item => selectedItems.has(item.id));
    if (items.length > 0) {
      onBook(items[0], items);
    }
  };

  const handleExport = async (format) => {
    if (selectedItems.size === 0) {
      alert('Выберите хотя бы одну позицию для экспорта');
      return;
    }

    setExporting(true);
    setShowExportMenu(false);

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL ||
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000');
      
      // Видимые колонки в порядке настроек таблицы
      const visibleColumns = columnOrder.filter(key => columnSettings[key]);
      const headers = visibleColumns.map(
        (key) => columns.find((col) => col.key === key)?.label || key
      );

      const payload = {
        [exportIdField]: Array.from(selectedItems),
        columns: visibleColumns,
        headers,
        format: format
      };
      
      const response = await axios.post(
        `${API_URL}${exportEndpoint}`,
        payload,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob'
        }
      );

      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const extension = format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `${exportFilenamePrefix}_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export error:', error);
      alert('Ошибка при экспорте файла');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    ...extraColumns.map((col) => ({ key: col.key, label: col.label })),
    { key: 'name', label: 'Наименование' },
    { key: 'vendor', label: 'Вендор' },
    { key: 'model', label: 'Модель' },
    { key: 'configuration', label: 'Конфигурация' },
    { key: 'description', label: 'Описание' },
    { key: 'quantity', label: 'Кол-во в наличии, шт.' },
    { key: 'available', label: 'Доступно' },
    { key: 'in_transit', label: 'В транзите' },
    { key: 'price_ddp', label: PRICE_COST_WITHOUT_VAT_RUB },
    { key: 'price_retail', label: PRICE_RECOMMENDED_RETAIL_RUB },
    { key: 'price_gpl', label: 'Цена GPL в валюте заказа по актуальному прайсу CNY' },
    { key: 'code_1c', label: extraKeys.includes('partner_name') ? 'Артикул' : 'КОД в 1С' },
    { key: 'category', label: allowBooking ? 'Тип' : 'Категория' },
    { key: 'location', label: 'Место' },
    { key: 'warehouse', label: 'Склад' },
    { key: 'comment', label: 'Комментарий' },
  ].filter((col) => !isHidden(col.key));

  // Обработчики для перетаскивания колонок
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== targetColumn) {
      const newOrder = [...columnOrder];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(targetColumn);
      
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedColumn);
      
      setColumnOrder(newOrder);
    }
    setDraggedColumn(null);
  };

  // Обработчики для изменения ширины колонок
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startWidth = columnWidths[columnKey];
    
    const handleMouseMove = (e) => {
      const newWidth = Math.max(50, startWidth + (e.clientX - startX));
      setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Функция для отображения содержимого ячейки с переносом
  const renderCellContent = (item, columnKey) => {
    const value = item[columnKey];
    
    if (columnKey === 'available') {
      return (
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
          item.available_quantity > 0 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {item.available_quantity}
        </span>
      );
    }
    
    if (columnKey === 'price_ddp') {
      return value ? `${value.toLocaleString('ru-RU')} ₽` : '-';
    }

    if (columnKey === 'price_retail') {
      return value != null && value !== '' ? `${value.toLocaleString('ru-RU')} ₽` : '-';
    }
    
    if (columnKey === 'price_gpl') {
      return value ? `${value.toLocaleString('ru-RU')} CNY` : '-';
    }
    
    if (columnKey === 'in_transit') {
      return value ? (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
          <FiTruck className="mr-1 h-3 w-3" />
          {value}
        </span>
      ) : (
        <span className="text-gray-400">-</span>
      );
    }
    
    if (columnKey === 'comment') {
      return value ? (
        <div
          className="text-sm text-amber-900 bg-amber-50 border-l-4 border-amber-400 rounded px-2 py-1 break-words whitespace-pre-line"
          title={value}
        >
          {value}
        </div>
      ) : (
        <span className="text-gray-400">-</span>
      );
    }

    // «Новое» — только на своём складе: склады партнёров перезаливаются целиком.
    if (columnKey === 'name' && allowBooking && isNewItem(item)) {
      return (
        <div className="text-sm text-gray-500 break-words whitespace-normal" title={value || ''}>
          <span className="mr-1 inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
            Новое
          </span>
          {value || '-'}
        </div>
      );
    }

    if (columnKey === 'description' || columnKey === 'configuration') {
      return (
        <div 
          className="text-sm text-gray-500 break-words whitespace-normal"
          style={{ 
            maxWidth: '100%',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
          title={value || ''}
        >
          {value || '-'}
        </div>
      );
    }
    
    return (
      <div 
        className="text-sm text-gray-500 break-words whitespace-normal"
        style={{ 
          maxWidth: '100%',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}
        title={value || ''}
      >
        {value || '-'}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Desktop Table View */}
      <div className="hidden lg:block" style={{ maxHeight: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header with settings */}
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-wrap gap-3 flex-shrink-0">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {title} ({equipmentList.length})
          </h3>
          {selectedItems.size > 0 && (
            <p className="text-sm text-gray-600 mt-1">
              Выбрано: <strong>{selectedItems.size}</strong> {positionsLabel(selectedItems.size)}
            </p>
          )}
        </div>
        
        <div className="flex gap-2">
          {/* Book selected button */}
          {allowBooking && selectedItems.size > 0 && (
            <button
              onClick={bookSelectedItems}
              className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
            >
              <FiCalendar className="mr-2" />
              Забронировать выбранное ({selectedItems.size})
            </button>
          )}

          {/* Export button */}
          {selectedItems.size > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={exporting}
                className="inline-flex items-center px-3 py-2 border border-green-300 shadow-sm text-sm font-medium rounded-md text-green-700 bg-white hover:bg-green-50 disabled:opacity-50"
              >
                {exporting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Экспорт...
                  </>
                ) : (
                  <>
                    <FiDownload className="mr-2" />
                    Экспорт для рассылки
                  </>
                )}
              </button>
              
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-10 border border-gray-200">
                  <div className="p-2">
                    <button
                      onClick={() => handleExport('excel')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                    >
                      <FiFileText className="mr-2 text-green-600" />
                      Экспорт в Excel
                    </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                    >
                      <FiFile className="mr-2 text-red-600" />
                      Экспорт в PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Settings button */}
          <div className="relative">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FiSettings className="mr-2" />
              Настройка колонок
            </button>
          
          {/* Settings dropdown */}
          {showSettings && (
            <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg z-10 border border-gray-200">
              <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium text-gray-900">Настройки колонок</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={showWidthSettings ? () => setShowWidthSettings(false) : openWidthSettings}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      {showWidthSettings ? 'Скрыть ширину' : 'Настроить ширину'}
                    </button>
                    <button
                      onClick={resetColumnWidths}
                      className="text-xs text-gray-600 hover:text-gray-700"
                    >
                      Сбросить
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {columnOrder.map(colKey => {
                    const col = columns.find(c => c.key === colKey);
                    const currentWidth = tempColumnWidths[colKey] !== undefined ? tempColumnWidths[colKey] : columnWidths[colKey];
                    return (
                      <div key={colKey} className="flex items-center justify-between">
                        <label className="flex items-center cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={columnSettings[colKey]}
                            onChange={() => toggleColumn(colKey)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
                          />
                          <span className="text-sm text-gray-700 truncate">{col?.label || colKey}</span>
                        </label>
                        
                        {showWidthSettings && (
                          <div className="flex items-center ml-2">
                            <input
                              type="number"
                              value={currentWidth}
                              onChange={(e) => updateTempColumnWidth(colKey, e.target.value)}
                              className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              min="50"
                              max="500"
                            />
                            <span className="text-xs text-gray-500 ml-1">px</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {showWidthSettings && (
                  <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-200">
                    <button
                      onClick={cancelWidthChanges}
                      className="px-3 py-1 text-xs text-gray-600 hover:text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Отмена
                    </button>
                    <button
                      onClick={applyWidthChanges}
                      className="px-3 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded"
                    >
                      Применить
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto flex-1" ref={tableRef} style={{ minHeight: '400px' }}>
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                <input
                  type="checkbox"
                  checked={selectedItems.size === equipmentList.length && equipmentList.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  title="Выбрать все"
                />
              </th>
              {columnOrder.map(colKey => {
                if (!columnSettings[colKey]) return null;
                const col = columns.find(c => c.key === colKey);
                return (
                  <th
                    key={colKey}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider relative select-none"
                    style={{ 
                      width: columnWidths[colKey],
                      maxWidth: columnWidths[colKey],
                      minWidth: columnWidths[colKey]
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, colKey)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, colKey)}
                  >
                    <div className="flex items-center justify-between">
                      <span 
                        className="break-words whitespace-normal leading-tight pr-2" 
                        style={{ 
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto'
                        }}
                      >
                        {col?.label || colKey}
                      </span>
                      <div className="flex items-center ml-1 flex-shrink-0">
                        <FiMove className="h-4 w-4 text-gray-400 cursor-move" />
                        <div
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500 hover:w-3 transition-all duration-200"
                          onMouseDown={(e) => handleMouseDown(e, colKey)}
                          title="Перетащите для изменения ширины"
                        />
                      </div>
                    </div>
                  </th>
                );
              })}
              {(allowBooking || onEdit) && (
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {equipmentList.length === 0 ? (
              <tr>
                <td colSpan={columnOrder.filter(c => columnSettings[c]).length + (allowBooking || onEdit ? 2 : 1)} className="px-6 py-8 text-center text-gray-500">
                  {emptyText}
                </td>
              </tr>
            ) : (
              equipmentList.map(item => (
                <tr key={item.id} className={`hover:bg-gray-50 ${selectedItems.has(item.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-2 py-4 text-center w-12">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => toggleSelectItem(item.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  {columnOrder.map(colKey => {
                    if (!columnSettings[colKey]) return null;
                    return (
                      <td 
                        key={colKey}
                        className="px-6 py-4 align-top"
                        style={{ 
                          width: columnWidths[colKey],
                          maxWidth: columnWidths[colKey],
                          minWidth: columnWidths[colKey]
                        }}
                      >
                        {renderCellContent(item, colKey)}
                      </td>
                    );
                  })}
                  {(allowBooking || onEdit) && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {onEdit && (
                      <button
                        onClick={() => onEdit(item)}
                        disabled={!canEdit}
                        title={canEdit ? 'Редактировать карточку товара' : 'Редактирование доступно только администратору'}
                        className="inline-flex items-center px-3 py-1 mr-2 border border-amber-300 text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FiEdit2 className="mr-1" />
                        {canEdit ? 'Редактировать' : 'Редактирование (админ)'}
                      </button>
                    )}
                    {allowBooking && (
                    <button
                      onClick={() => onBook(item)}
                      disabled={item.available_quantity === 0}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiCalendar className="mr-1" />
                      Забронировать
                    </button>
                    )}
                  </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden">
        {/* Mobile Header */}
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {title} ({equipmentList.length})
          </h3>
          {selectedItems.size > 0 && (
            <p className="text-sm text-gray-600 mt-1">
              Выбрано: <strong>{selectedItems.size}</strong> {positionsLabel(selectedItems.size)}
            </p>
          )}
        </div>

        {/* Mobile Cards */}
        <div className="divide-y divide-gray-200 max-h-[calc(100vh-250px)] overflow-y-auto">
          {equipmentList.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              {emptyText}
            </div>
          ) : (
            equipmentList.map(item => (
              <div 
                key={item.id} 
                className={`p-4 ${selectedItems.has(item.id) ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start flex-1">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => toggleSelectItem(item.id)}
                      className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="ml-3 flex-1">
                      <h4 className="text-base font-semibold text-gray-900">{item.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {item.vendor} {item.model && `• ${item.model}`}
                      </p>
                      {allowBooking && item.category && <p className="text-xs text-gray-500 mt-0.5">{item.category}</p>}
                      <EquipmentComment text={item.comment} compact className="mt-2" />
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {allowBooking && isNewItem(item) && (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        Новое
                      </span>
                    )}
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      (item.available_quantity ?? item.quantity) > 0 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {allowBooking ? `Доступно: ${item.available_quantity}` : `В наличии: ${item.quantity || 0}`}
                    </span>
                    {item.partner_name && (
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                        {item.partner_name}
                      </span>
                    )}
                    {item.in_transit && (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                        <FiTruck className="mr-1 h-3 w-3" />
                        В транзите: {item.in_transit}
                      </span>
                    )}
                  </div>
                </div>
                
                {item.configuration && (
                  <p className="text-sm text-gray-600 mt-2">
                    <strong>Конфигурация:</strong> {item.configuration}
                  </p>
                )}
                
                {item.description && (
                  <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                    {item.description}
                  </p>
                )}
                
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-600">
                  {item.quantity && (
                    <span>Всего: <strong>{item.quantity}</strong></span>
                  )}
                  {item.price_ddp && (
                    <span>{PRICE_COST_WITHOUT_VAT}: <strong>{item.price_ddp.toLocaleString('ru-RU')} ₽</strong></span>
                  )}
                  {item.price_retail != null && item.price_retail !== '' && (
                    <span>{PRICE_RECOMMENDED_RETAIL}: <strong>{item.price_retail.toLocaleString('ru-RU')} ₽</strong></span>
                  )}
                  {item.price_gpl && (
                    <span>Цена GPL: <strong>{item.price_gpl.toLocaleString('ru-RU')} CNY</strong></span>
                  )}
                  {item.code_1c && (
                    <span>1С: <strong className="font-mono">{item.code_1c}</strong></span>
                  )}
                </div>
                
                {allowBooking && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => onBook(item)}
                    disabled={item.available_quantity === 0}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiCalendar className="mr-1" />
                    Забронировать
                  </button>
                </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Mobile Actions */}
        {selectedItems.size > 0 && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex gap-2">
            {allowBooking && (
            <button
              onClick={bookSelectedItems}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
            >
              <FiCalendar className="mr-2" />
              Забронировать ({selectedItems.size})
            </button>
            )}
            <button
              onClick={() => handleExport('excel')}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-green-300 shadow-sm text-sm font-medium rounded-md text-green-700 bg-white hover:bg-green-50"
            >
              <FiDownload className="mr-2" />
              Excel ({selectedItems.size})
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FiSettings />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default EquipmentTable;